import { useEffect, useState } from "react";
import { X, Sparkles, Copy, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { client, PLATFORMS } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { markCoachStep } from "@/lib/coach";
import { useAuth } from "@/lib/auth";

const TONES = [
  { id: "conversational", label: "Conversational" },
  { id: "punchy", label: "Punchy" },
  { id: "educational", label: "Educational" },
  { id: "storytelling", label: "Storytelling" },
  { id: "inspirational", label: "Inspirational" },
];

const LENGTHS = [
  { id: "short", label: "Short" },
  { id: "medium", label: "Medium" },
  { id: "long", label: "Long" },
];

function MarkdownPreview({ text }) {
  // Lightweight md renderer for headings, bold, italics, line breaks
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let para = [];
  const flush = () => {
    if (para.length) {
      blocks.push(
        <p key={`p${blocks.length}`} className="text-sm leading-relaxed mb-3" style={{ color: "var(--ink-72)" }}>
          {renderInline(para.join(" "))}
        </p>
      );
      para = [];
    }
  };
  const renderInline = (s) => {
    // bold **x** + italics *x* + brackets [x]
    const parts = [];
    let rest = s;
    let key = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\])/;
    while (rest) {
      const m = rest.match(re);
      if (!m) { parts.push(rest); break; }
      parts.push(rest.slice(0, m.index));
      const t = m[0];
      if (t.startsWith("**")) parts.push(<strong key={`b${key++}`} style={{ color: "var(--ink)" }}>{t.slice(2, -2)}</strong>);
      else if (t.startsWith("*")) parts.push(<em key={`i${key++}`} className="font-display-italic" style={{ color: "var(--ink)" }}>{t.slice(1, -1)}</em>);
      else parts.push(<span key={`k${key++}`} className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: "rgba(216,168,87,0.12)", color: "var(--seal)" }}>{t.slice(1, -1)}</span>);
      rest = rest.slice(m.index + t.length);
    }
    return parts;
  };
  lines.forEach((ln, idx) => {
    if (ln.startsWith("## ")) {
      flush();
      blocks.push(
        <h3 key={`h${idx}`} className="font-heading text-xl mt-5 mb-2" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500", color: "var(--ink)" }}>
          {ln.slice(3)}
        </h3>
      );
    } else if (ln.startsWith("### ")) {
      flush();
      blocks.push(
        <h4 key={`h${idx}`} className="text-xs uppercase tracking-[0.22em] mt-4 mb-1.5" style={{ color: "var(--seal)" }}>
          {ln.slice(4)}
        </h4>
      );
    } else if (ln.trim() === "") {
      flush();
    } else {
      para.push(ln);
    }
  });
  flush();
  return <div>{blocks}</div>;
}

export default function ScriptModal({ idea, onClose, onSaved }) {
  const { user, refresh } = useAuth();
  const quota = user?.ai_quota;
  const [topic, setTopic] = useState(idea?.title || "");
  const [platform, setPlatform] = useState(idea?.platform || "youtube_long");
  const [tone, setTone] = useState("conversational");
  const [length, setLength] = useState("medium");
  const [audience, setAudience] = useState("");
  const [extras, setExtras] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    track(EVENTS.AI_COPILOT_OPENED, { has_idea: !!idea });
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await client.post("/ai/script", {
        idea_id: idea?.id || null,
        topic: topic.trim(),
        platform,
        tone,
        length,
        audience,
        extras,
      });
      track(EVENTS.AI_SCRIPT_GENERATED, { platform, tone, length, attached_to_idea: !!idea });
      setResult(r.data);
      markCoachStep("generate-script");
      onSaved?.(r.data);
      // Refresh user to update the ai_quota counter in the UI.
      refresh?.();
      // Toast notification — clear signal that script is saved and findable.
      toast.success("Script ready!", {
        description: "Auto-saved to your Scripts library.",
        duration: 6000,
        action: {
          label: "Open library",
          onClick: () => { window.location.href = "/scripts"; },
        },
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "ai_quota_exceeded") {
        setError(`${detail.message} (You've used ${detail.used} of ${detail.limit} this month.)`);
      } else {
        setError(detail || "Generation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.content) return;
    await navigator.clipboard.writeText(result.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center"
      onClick={onClose}
      data-testid="script-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="script-modal"
        className="rounded-t-2xl md:rounded-2xl w-full md:max-w-3xl max-h-[94vh] overflow-y-auto"
        style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
          style={{ background: "var(--surface)", borderBottom: "1px solid var(--ink-22)" }}
        >
          <div className="flex items-center gap-2.5">
            <Sparkles size={16} style={{ color: "var(--seal)" }} />
            <h3 className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
              AI <span className="font-display-italic">Co-Pilot</span>
            </h3>
            {quota && (
              <span
                className="ml-2 hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em]"
                style={{
                  background: quota.unlimited ? "rgba(216,168,87,0.10)" : (quota.remaining === 0 ? "rgba(201,122,106,0.10)" : "rgba(216,168,87,0.10)"),
                  border: `1px solid ${quota.unlimited ? "rgba(216,168,87,0.40)" : (quota.remaining === 0 ? "rgba(201,122,106,0.30)" : "rgba(216,168,87,0.30)")}`,
                  color: quota.unlimited ? "var(--seal)" : (quota.remaining === 0 ? "#e8a999" : "var(--seal)"),
                }}
                data-testid="script-quota-badge"
              >
                {quota.unlimited ? "Unlimited" : `${quota.used}/${quota.limit} this month`}
              </span>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="script-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="eyebrow block mb-2">Topic / Hook</label>
            <input
              data-testid="script-topic-input"
              autoFocus
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What is the script about?"
              className="input text-base"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32, 'wght' 450" }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="eyebrow block mb-2">Platform</label>
              <div className="flex flex-wrap gap-1.5">
                {PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`script-platform-${p.id}`}
                    onClick={() => setPlatform(p.id)}
                    className="px-3 py-1.5 rounded-full text-xs transition"
                    style={{
                      background: platform === p.id ? "var(--ink)" : "transparent",
                      color: platform === p.id ? "var(--bg)" : "var(--ink-55)",
                      border: platform === p.id ? "1px solid var(--ink)" : "1px solid var(--ink-22)",
                    }}
                  >
                    {p.short}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="eyebrow block mb-2">Tone</label>
              <select
                data-testid="script-tone-select"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="input"
              >
                {TONES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="eyebrow block mb-2">Length</label>
              <div className="flex gap-1.5">
                {LENGTHS.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`script-length-${l.id}`}
                    onClick={() => setLength(l.id)}
                    className="px-3.5 py-1.5 rounded-full text-xs transition flex-1"
                    style={{
                      background: length === l.id ? "var(--ink)" : "transparent",
                      color: length === l.id ? "var(--bg)" : "var(--ink-55)",
                      border: length === l.id ? "1px solid var(--ink)" : "1px solid var(--ink-22)",
                    }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Audience <span className="normal-case tracking-normal" style={{ color: "var(--ink-38)" }}>(optional)</span></label>
              <input
                data-testid="script-audience-input"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="e.g., new creators, 18-30"
                className="input"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Extras <span className="normal-case tracking-normal" style={{ color: "var(--ink-38)" }}>(optional)</span></label>
              <input
                data-testid="script-extras-input"
                value={extras}
                onChange={(e) => setExtras(e.target.value)}
                placeholder="any angle, story, stat to include…"
                className="input"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm p-3 rounded-lg" style={{ background: "rgba(201,122,106,0.10)", color: "#e8a999", border: "1px solid rgba(201,122,106,0.30)" }}>
              {error}
            </div>
          )}

          {result && (
            <div className="rounded-xl p-5 mt-2" style={{ background: "var(--bg)", border: "1px solid var(--ink-22)" }} data-testid="script-result">
              <div
                className="flex items-center gap-2 mb-3 px-3 py-2 rounded-full text-xs"
                style={{ background: "rgba(216,168,87,0.12)", border: "1px solid rgba(216,168,87,0.40)", color: "var(--seal)" }}
                data-testid="script-ready-banner"
              >
                <Check size={14} />
                <span className="font-medium">Script ready — saved to your library</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Generated script · {result.model || "gpt-5.2"}</div>
                <button onClick={copy} className="btn-ghost text-xs" data-testid="script-copy-btn">
                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <MarkdownPreview text={result.content} />
            </div>
          )}
        </div>

        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 md:px-6 py-4 sticky bottom-0"
          style={{ background: "var(--surface)", borderTop: "1px solid var(--ink-22)", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="text-[11px] flex flex-wrap items-center gap-2" style={{ color: "var(--ink-55)" }}>
            {idea ? (
              <span className="truncate max-w-[260px] md:max-w-none">
                Attached to <span style={{ color: "var(--ink)" }}>{idea.title.slice(0, 40)}{idea.title.length > 40 ? "…" : ""}</span>
              </span>
            ) : null}
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <button onClick={onClose} className="btn-secondary flex-1 md:flex-initial" data-testid="script-cancel-btn">Close</button>
            <button
              onClick={generate}
              disabled={loading || !topic.trim()}
              className="btn-primary disabled:opacity-50 flex-1 md:flex-initial"
              data-testid="script-generate-btn"
            >
              <Wand2 size={14} /> {loading ? "Writing…" : result ? "Regenerate" : "Generate script"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
